import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  Anchor,
  CheckCircle2, 
  Clock, 
  MapPin, 
  Ship, 
  User, 
  Phone, 
  Users, 
  CreditCard,
  Calendar,
  Loader2,
  AlertCircle,
  Home,
  Navigation,
  Banknote
} from "lucide-react";
import axios from "axios";
import { useNotifications } from "@/contexts/NotificationContext";
import { NotificationBell, PWAInstallPrompt } from "@/components/NotificationBell";

import { API_URL } from "@/config/api";

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentChecked, setPaymentChecked] = useState(false);
  const notificationShownRef = useRef(false);
  const { 
    notifications,
    notifyBookingConfirmed, 
    notifyDriverAssigned,
    markAllAsRead,
    clearAllNotifications
  } = useNotifications();

  const bookingId = searchParams.get("booking_id");
  const sessionId = searchParams.get("session_id");

  // Poll payment status if session_id exists
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || paymentChecked) return;

      try {
        // Poll status up to 5 times
        let attempts = 0;
        const maxAttempts = 5;
        
        while (attempts < maxAttempts) {
          const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setPaymentChecked(true);
            break;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        setPaymentChecked(true);
      } catch (error) {
        console.error("Payment status check failed:", error);
        setPaymentChecked(true);
      }
    };

    checkPaymentStatus();
  }, [sessionId, paymentChecked]);

  // Fetch booking details
  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      // Wait a bit for payment to be processed
      if (sessionId && !paymentChecked) {
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
        setBooking(response.data);
      } catch (error) {
        console.error("Failed to fetch booking:", error);
        setError("Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, sessionId, paymentChecked]);

  // Show in-app notifications when booking data is loaded
  useEffect(() => {
    if (booking && !notificationShownRef.current) {
      notificationShownRef.current = true;
      
      // Show booking confirmation notification
      notifyBookingConfirmed({
        booking_id: booking.booking_id,
        tracking_token: booking.tracking_token || booking.client_tracking_token
      });

      // Show driver assigned notification if applicable
      if (booking.assigned_driver_id) {
        setTimeout(() => {
          notifyDriverAssigned({
            booking_id: booking.booking_id,
            driver_name: booking.assigned_driver_name
          });
        }, 1500); // Slight delay for better UX
      }
    }
  }, [booking, notifyBookingConfirmed, notifyDriverAssigned]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Error</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-orange-500 hover:bg-orange-600">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = booking?.payment_status === "paid";
  const isPending = booking?.payment_status === "pending";
  const isPayToDriver = booking?.payment_method === "pay_to_driver" || booking?.payment_status === "pay_on_site";

  // Get tracking token from booking if available
  const trackingToken = booking?.tracking_token || booking?.client_tracking_token;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="container-app">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            {/* Notification Bell */}
            <NotificationBell 
              notifications={notifications}
              onClear={clearAllNotifications}
              onMarkAllRead={markAllAsRead}
            />
          </div>
        </div>
      </header>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      <div className="container-app py-12">
        <div className="max-w-xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-slate-600">
              Your shuttle booking has been successfully created.
            </p>
          </div>

          {/* Booking Details Card */}
          <Card className="animate-slide-up">
            <CardContent className="pt-6">
              {/* Booking ID */}
              <div className="text-center mb-6 p-4 bg-slate-50 rounded-md">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Booking Reference</p>
                <p className="text-2xl font-mono font-bold text-orange-500">{booking?.booking_id}</p>
              </div>

              {/* Payment Status Badge - Improved Clarity */}
              <div className="flex justify-center mb-6">
                {isPaid && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    Paid Online ✓
                  </span>
                )}
                {isPending && !isPayToDriver && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    Payment Pending
                  </span>
                )}
                {isPayToDriver && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    <Banknote className="w-4 h-4" />
                    Pay Driver on Arrival
                  </span>
                )}
              </div>

              <Separator className="my-6" />

              {/* Booking Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Ship className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Ship</p>
                    <p className="font-medium">{booking?.ship_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Terminal</p>
                    <p className="font-medium">{booking?.terminal_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-orange-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pickup Address</p>
                    <p className="font-medium text-sm">{booking?.pickup_address || "Not specified"}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Passenger</p>
                    <p className="font-medium">{booking?.passenger_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Phone</p>
                    <p className="font-medium">{booking?.phone_number}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Passengers</p>
                    <p className="font-medium">{booking?.passengers} <span className="text-slate-400 text-sm">(info only)</span></p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pickup</p>
                    <p className="font-medium">
                      {booking?.pickup_datetime && new Date(booking.pickup_datetime).toLocaleString('en-GB', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </p>
                  </div>
                </div>

                {booking?.driver_notes && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-orange-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Driver Notes</p>
                      <p className="font-medium text-sm">{booking.driver_notes}</p>
                    </div>
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              {/* Price */}
              <div className="text-center p-4 bg-orange-50 rounded-md">
                <p className="text-xs text-orange-600 uppercase tracking-wider mb-1">Vehicle Price</p>
                <p className="text-3xl font-mono font-bold text-orange-600">
                  €{booking?.final_price?.toFixed(2)}
                </p>
                {booking?.night_surcharge_applied && (
                  <p className="text-xs text-orange-500 mt-1">Includes night surcharge (+20%)</p>
                )}
                <p className="text-xs text-slate-500 mt-2">Price per vehicle — not per person</p>
              </div>

              <Separator className="my-6" />

              {/* Next Steps */}
              <div className="bg-slate-50 p-4 rounded-md">
                <h3 className="font-bold text-slate-900 mb-3">What's Next?</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You'll receive a WhatsApp confirmation shortly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>A driver will be assigned to your booking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Track your driver in real-time when they're on the way</span>
                  </li>
                  {isPayToDriver && (
                    <li className="flex items-start gap-2">
                      <Banknote className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>Please have €{booking?.final_price?.toFixed(2)} ready for the driver</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Driver Assignment Status */}
              <div className="mt-4 p-4 border-2 border-dashed border-slate-200 rounded-lg">
                <div className="flex items-center gap-3">
                  {booking?.assigned_driver_id ? (
                    <>
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-800">Driver Assigned</p>
                        <p className="text-sm text-slate-600">{booking?.assigned_driver_name || "Your driver is ready"}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-orange-600 animate-pulse" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">Driver Assignment Pending</p>
                        <p className="text-sm text-slate-500">We're assigning the best driver for you</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 mt-6">
                {/* Track Your Driver Button - Always visible */}
                <Link to={trackingToken ? `/track/${trackingToken}` : `/track/${booking?.booking_id}`}>
                  <Button 
                    data-testid="track-driver-btn"
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Track Your Driver
                  </Button>
                </Link>
                
                <Link to="/">
                  <Button 
                    data-testid="go-home-btn"
                    variant="outline"
                    className="w-full"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Questions? Contact us at{" "}
            <a href="mailto:booking@y-go.fr" className="text-orange-500 hover:underline">
              booking@y-go.fr
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
